// The preview's scrub cache (spec docs/specs/editor/instant-scrub.md): each video file of the
// project read by mediabunny over ranged reads, and the spans its clips show read ahead while the
// preview is paused, nearest the playhead first and within a budget. A seek or a drag then asks for
// its exact frame, which is decoded from memory in tens of milliseconds, where a file's host takes
// half a second to its first byte. Playing reads nothing: the reads in flight stop at Play and are
// read again once paused, so the clips' elements have the network (spec stalled-element).
import type { CanvasSink, Input, InputVideoTrack } from "mediabunny";

import { stoppableFetch, type RangeFetch } from "../../../api/download";
import { mediaCredentials } from "../../../api/media-cookie";
import type { EngineProject } from "./project";
import type { Picture } from "./scene";

/** A file's span a video clip shows, and where the clip is on the timeline (ms). */
export interface ScrubSpan {
  url: string;
  fromMs: number;
  toMs: number;
  startMs: number;
  endMs: number;
}

/** A video layer's ask for its exact frame: its clip, its file and the time in the file (ms). */
export interface ScrubAsk {
  clipId: string;
  url: string;
  ms: number;
}

/**
 * How many bytes of the project's files the cache holds at most, counted as they arrive. mediabunny
 * reads on past each span (a sequential read's region doubles, in steps of 8 MiB), so a two-second
 * span of a 15 Mbps phone file holds up to 16 MiB: dev's 60 s project of 30 such clips held 481 MB.
 */
export const WARM_BUDGET_BYTES = 512 * 2 ** 20;
/** How wide a decoded frame is at most: the preview draws a 1280-wide frame. */
export const SCRUB_WIDTH = 1280;
/** How many spans are read ahead at once: one file's host answers each after its own wait. */
export const READS_AT_ONCE = 3;
/** How far past its end a span is read: a frame shown before a later one may be stored after it. */
export const READ_PAST_MS = 250;
/** A strip frame's longer side at most (spec scrub-strip): a drag's picture until the exact frame. */
export const STRIP_SIZE = 320;
/** How far apart a span's strip frames are at least, in ms of its file. */
export const STRIP_STEP_MS = 2000;
/** How many strip frames are kept at most: about 230 KB each. */
export const STRIP_FRAMES = 120;
/** How many strips are built at once: each waits on its file's host far more than on the network's speed. */
export const STRIPS_AT_ONCE = 3;
/** What a strip frame's read counts against the budget: half a second of its file (mediabunny reads 0.5 MiB at least). */
const STRIP_READ_MS = 500;
/** Why the cache's reads stop: the preview plays. A read stopped so is read again once paused. */
export const STOPPED_FOR_PLAY = new DOMException("The preview plays", "AbortError");
/** What a fetch the network refused says, in Chrome, Safari and Firefox. */
const REFUSED = /Failed to fetch|Load failed|NetworkError when attempting to fetch resource/;

/**
 * Whether a failed read is the stop for playing. mediabunny's own aborts never reach its retry rule
 * or its handler, so an abort there is the stop: with its reason, or as a plain abort where a
 * browser passes no reason through a response's body.
 */
const isStop = (error: unknown) =>
  error === STOPPED_FOR_PLAY || (error instanceof DOMException && error.name === "AbortError");

/**
 * When mediabunny tries a failed read again, in seconds, or null for never. One stopped for playing
 * is not tried again: it is read again once paused. Any other failure is treated as mediabunny's
 * default treats it: a fetch another origin refused while online is taken for CORS and left; else
 * it is tried again after 0.5 s, then twice as long each time, at most 16 s.
 */
export function retryDelay(attempts: number, error: unknown, url: string | URL | Request): number | null {
  if (isStop(error)) return null;
  if (error instanceof Error && REFUSED.test(error.message) && navigator.onLine && elsewhere(url)) return null;
  return Math.min(2 ** (attempts - 2), 16);
}

/** Whether `url` is on another origin than the page. */
function elsewhere(url: string | URL | Request): boolean {
  try {
    return new URL(url instanceof Request ? url.url : url, location.href).origin !== location.origin;
  } catch {
    return false;
  }
}

/** Whether a read that failed was stopped for playing: by the stop itself, or Play came meanwhile. */
const stoppedForPlay = (error: unknown, signal: AbortSignal) => isStop(error) || signal.aborted;

/** The file spans the project's video clips show, one per clip, in the project's order. */
export function scrubSpans(project: EngineProject): ScrubSpan[] {
  return project.layers
    .filter((layer) => layer.kind === "video")
    .map((layer) => ({
      url: layer.url,
      fromMs: layer.inMs,
      toMs: layer.inMs + (layer.endMs - layer.startMs) * layer.speed,
      startMs: layer.startMs,
      endMs: layer.endMs,
    }));
}

/**
 * How far a span is from the playhead on the timeline: 0 when the playhead is in it, and a span
 * that ends at the playhead 1 ms (the playhead at a cut is in the clip that starts there).
 */
function distance(span: ScrubSpan, atMs: number): number {
  if (atMs < span.startMs) return span.startMs - atMs;
  return atMs >= span.endMs ? atMs - span.endMs + 1 : 0;
}

/** The spans in the order to read them ahead: nearest the playhead first, then the earlier. */
export function warmOrder(spans: readonly ScrubSpan[], atMs: number): ScrubSpan[] {
  return [...spans].sort((one, other) => distance(one, atMs) - distance(other, atMs) || one.startMs - other.startMs);
}

/**
 * The spans to read within the budget, in order: each span's bytes estimated from its file's
 * bytes per ms; the first one that would pass the budget ends the list.
 */
export function withinBudget(
  spans: readonly ScrubSpan[],
  bytesPerMs: (url: string) => number,
  budget: number = WARM_BUDGET_BYTES,
): ScrubSpan[] {
  const chosen: ScrubSpan[] = [];
  let spent = 0;
  for (const span of spans) {
    spent += (span.toMs - span.fromMs) * bytesPerMs(span.url);
    if (spent > budget) break;
    chosen.push(span);
  }
  return chosen;
}

/**
 * The times (ms of its file) of a span's strip frames, from its file's keyframe times around it, in
 * order: its first keyframe inside it, or the one before it when none is inside, then each keyframe
 * at least `stepMs` after the last one taken.
 */
export function stripTimes(keys: readonly number[], span: ScrubSpan, stepMs: number = STRIP_STEP_MS): number[] {
  const inside = keys.filter((ms) => ms >= span.fromMs && ms < span.toMs);
  if (inside.length === 0) {
    const before = keys.filter((ms) => ms < span.fromMs).at(-1);
    return before === undefined ? [] : [before];
  }
  const times: number[] = [];
  for (const ms of inside) if (ms - (times.at(-1) ?? -Infinity) >= stepMs) times.push(ms);
  return times;
}

type Mediabunny = typeof import("mediabunny");

/** A file opened for scrubbing: its video track, its exact and its strip frames, its bytes per ms. */
interface Opened {
  track: InputVideoTrack;
  frames: CanvasSink;
  strip: CanvasSink;
  bytesPerMs: number;
}

/** A span's strip: the span of its file, and its frames in time order. */
interface Strip {
  url: string;
  fromMs: number;
  toMs: number;
  frames: { ms: number; bitmap: ImageBitmap; picture: Picture }[];
}

/**
 * A clip's frame decoded last: its file, the time asked, the file's times it shows (a time past the
 * last frame gets the last frame), its bitmap.
 */
interface Decoded {
  url: string;
  askedMs: number;
  fromMs: number;
  toMs: number;
  bitmap: ImageBitmap;
  picture: Picture;
}

const answers = (decoded: Decoded, ask: ScrubAsk) =>
  decoded.url === ask.url && (ask.ms === decoded.askedMs || (ask.ms >= decoded.fromMs && ask.ms < decoded.toMs));

const spanKey = (span: ScrubSpan) => `${span.fromMs}-${span.toMs}`;
const readingKey = (span: ScrubSpan) => `${span.url} ${spanKey(span)}`;

/** A file read by the cache: its input, what it opened to, what it holds, its decode. */
class ScrubFile {
  /** The bytes read from its host, which its cache keeps (up to the budget, the least used out first). */
  held = 0;
  /** The spans read ahead so far ("fromMs-toMs"), each with the ms read for it from its keyframe. */
  readonly read = new Map<string, number>();
  decoding = false;
  readonly input: Promise<Input>;
  /** Null when it cannot be opened: no file there, no video in it, or a refused read. */
  readonly opened: Promise<Opened | null>;

  constructor(url: string, library: Promise<Mediabunny>, budget: number, fetchFn: RangeFetch) {
    this.input = library.then((mb) => {
      // A file's own URL goes with the media cookie and a signed one without, as the elements read them.
      // Its reads go at low priority (spec scrub-priority): a fetch is high by default, and the
      // media host serves higher first, so the read ahead held back the clips' own (media, low)
      // reads on dev, a clip's index for 40 s. `fetchFn` stops its reads while the preview plays;
      // a read stopped so is not tried again.
      const source = new mb.UrlSource(url, {
        maxCacheSize: budget,
        requestInit: { credentials: mediaCredentials(url), priority: "low" },
        fetchFn,
        getRetryDelay: retryDelay,
        // A stopped read nothing waits for (mediabunny reads on past what is asked) is no error; any
        // other stays an unhandled rejection, as mediabunny leaves it.
        handleUnhandledError: (error) => {
          if (!isStop(error)) throw error;
        },
      });
      source.on("read", ({ start, end }) => {
        this.held += end - start;
      });
      return new mb.Input({ source, formats: mb.ALL_FORMATS });
    });
    this.opened = Promise.all([this.input, library])
      .then(async ([input, mb]) => {
        const track = await input.getPrimaryVideoTrack();
        if (track === null) return null;
        const [size, seconds] = await Promise.all([input.source.getSize(), track.computeDuration()]);
        // One decode per file, kept: a new one would set its decoder up again for every seek.
        const frames = new mb.CanvasSink(track, { width: Math.min(SCRUB_WIDTH, track.displayWidth) });
        const scale = Math.min(1, STRIP_SIZE / Math.max(track.displayWidth, track.displayHeight));
        const strip = new mb.CanvasSink(track, { width: Math.max(1, Math.round(track.displayWidth * scale)) });
        return { track, frames, strip, bytesPerMs: seconds > 0 ? size / (seconds * 1000) : 0 };
      })
      .catch(() => null);
  }
}

/**
 * The project's files for scrubbing. `warm` reads ahead from the playhead while paused, `rest`
 * stops every read while playing, and `frames` gives a paint's exact frames once decoded, asking for the
 * others (`onFrame` when one is). Without WebCodecs (an old browser, jsdom) it does nothing, and the
 * preview shows what it did before.
 */
export class ScrubCache {
  private readonly files = new Map<string, ScrubFile>();
  /** Each file's bytes per ms, learnt by opening it once, and the files that cannot be opened. */
  private readonly rates = new Map<string, number>();
  private readonly unreadable = new Set<string>();
  private library: Promise<Mediabunny> | null = null;
  private resting = true;
  private reading = false;
  private stripping = false;
  private order: ScrubSpan[] = [];
  /** The files of the clips at the playhead: kept, since a seek or a drag there decodes from them. */
  private here = new Set<string>();
  /** The spans being read now ("url fromMs-toMs"), and the bytes kept for them until they are. */
  private readonly inFlight = new Set<string>();
  private reserved = 0;
  /** Each clip's frame decoded last, and its latest ask still to decode, by clip. */
  private readonly decoded = new Map<string, Decoded>();
  private readonly wanted = new Map<string, ScrubAsk>();
  /** The last paint's asks: a strip done after them is told (`onFrame`), as a decoded frame is. */
  private asks: readonly ScrubAsk[] = [];
  /** The spans' strips, by span ("url fromMs-toMs"), and how many frames they hold together. */
  private readonly strips = new Map<string, Strip>();
  private stripped = 0;
  /** The decodes running for a seek, and the readers waiting for them to be done. */
  private asked = 0;
  private readonly waiting: (() => void)[] = [];
  private disposed = false;
  /** Aborted at Play, and new once paused: the files' reads go through it (`fetchFn`). */
  private stop = new AbortController();
  private readonly fetchFn = stoppableFetch(() => this.stop.signal);

  constructor(
    private readonly load: () => Promise<Mediabunny>,
    private readonly onFrame: () => void,
    private readonly budget: number = WARM_BUDGET_BYTES,
  ) {}

  /** Whether this browser decodes frames itself (WebCodecs). */
  static supported(): boolean {
    return typeof VideoDecoder !== "undefined" && typeof createImageBitmap === "function";
  }

  /** Paused at `atMs`: read the clips' spans ahead from there, nearest first. */
  warm(project: EngineProject, atMs: number): void {
    if (this.disposed || !ScrubCache.supported()) return;
    const spans = scrubSpans(project);
    // A file no clip shows any more is let go (its URL also changes when the URLs are read again).
    const used = new Set(spans.map((span) => span.url));
    for (const [url, file] of this.files) if (!used.has(url)) this.drop(url, file);
    for (const url of this.rates.keys()) if (!used.has(url)) this.rates.delete(url);
    for (const url of this.unreadable) if (!used.has(url)) this.unreadable.delete(url);
    const shown = new Set(spans.map(readingKey));
    for (const [key, strip] of this.strips) if (!shown.has(key)) this.dropStrip(key, strip);
    this.order = warmOrder(spans, atMs);
    this.here = new Set(spans.filter((span) => distance(span, atMs) === 0).map((span) => span.url));
    this.reconcile(this.working());
    // Paused again: what Play stopped is read again.
    if (this.stop.signal.aborted) this.stop = new AbortController();
    this.resting = false;
    if (!this.reading) void this.readAhead();
    if (!this.stripping) void this.stripAhead();
  }

  /**
   * Playing: no more reading ahead and no frames kept, and the files' reads in flight stop at once
   * (any read until paused fails at once), so the clips' elements have the network to themselves.
   */
  rest(): void {
    this.resting = true;
    this.stop.abort(STOPPED_FOR_PLAY);
    this.forget(new Set());
    for (const done of this.waiting.splice(0)) done();
  }

  /**
   * A paused paint's exact frames, by clip, those decoded so far. The others are asked for, each
   * clip's latest ask, and `onFrame` tells when one is decoded; meanwhile a clip with no frame of its
   * file decoded yet gets its strip's frame, if it has one. A clip that asks no more (its element is
   * at its frame, or it is not showing) lets its frame go.
   */
  frames(asks: readonly ScrubAsk[]): Map<string, Picture> {
    const pictures = new Map<string, Picture>();
    if (this.disposed || !ScrubCache.supported()) return pictures;
    this.forget(new Set(asks.map((ask) => ask.clipId)));
    this.asks = asks;
    for (const ask of asks) {
      const decoded = this.decoded.get(ask.clipId);
      if (decoded !== undefined && answers(decoded, ask)) {
        pictures.set(ask.clipId, decoded.picture);
        this.wanted.delete(ask.clipId);
      } else if (!this.unreadable.has(ask.url)) {
        this.wanted.set(ask.clipId, ask);
        const file = this.file(ask.url);
        if (!file.decoding) void this.decode(ask.url, file);
        // A clip with a frame of its file decoded keeps showing it (the hold); one without, its strip's.
        const near = decoded?.url === ask.url ? undefined : this.stripFrame(ask);
        if (near !== undefined) pictures.set(ask.clipId, near);
      }
    }
    return pictures;
  }

  dispose(): void {
    this.disposed = true;
    this.forget(new Set());
    for (const done of this.waiting.splice(0)) done();
    for (const [url, file] of this.files) this.drop(url, file);
    for (const [key, strip] of this.strips) this.dropStrip(key, strip);
  }

  /** The strip frame of the asking clip's span at or before the asked time, else its first one. */
  private stripFrame(ask: ScrubAsk): Picture | undefined {
    for (const strip of this.strips.values()) {
      if (strip.url !== ask.url || ask.ms < strip.fromMs || ask.ms >= strip.toMs || strip.frames.length === 0) continue;
      return (strip.frames.filter((frame) => frame.ms <= ask.ms).at(-1) ?? strip.frames[0])?.picture;
    }
    return undefined;
  }

  private dropStrip(key: string, strip: Strip): void {
    for (const frame of strip.frames) frame.bitmap.close();
    this.stripped -= strip.frames.length;
    this.strips.delete(key);
  }

  private mediabunny(): Promise<Mediabunny> {
    this.library ??= this.load();
    return this.library;
  }

  private file(url: string): ScrubFile {
    const known = this.files.get(url);
    if (known !== undefined) return known;
    const file = new ScrubFile(url, this.mediabunny(), this.budget, this.fetchFn);
    const { signal } = this.stop;
    this.files.set(url, file);
    void file.opened.then((opened) => {
      if (opened !== null) this.rates.set(url, opened.bytesPerMs);
      // A file let go fails its opening too: only the one still in use is found unreadable. One
      // whose opening Play stopped is let go instead, to be opened again once paused.
      else if (this.files.get(url) === file) {
        if (signal.aborted) this.drop(url, file);
        else this.unreadable.add(url);
      }
    });
    return file;
  }

  private drop(url: string, file: ScrubFile): void {
    this.files.delete(url);
    void file.input.then((input) => input.dispose()).catch(() => undefined);
  }

  /** Lets the frames and the asks of the clips not in `asking` go. */
  private forget(asking: ReadonlySet<string>): void {
    for (const [clipId, decoded] of this.decoded) {
      if (asking.has(clipId)) continue;
      decoded.bitmap.close();
      this.decoded.delete(clipId);
    }
    for (const clipId of this.wanted.keys()) if (!asking.has(clipId)) this.wanted.delete(clipId);
    this.asks = this.asks.filter((ask) => asking.has(ask.clipId));
  }

  /** The first ask still to decode from the file at `url`. */
  private askOf(url: string): ScrubAsk | undefined {
    for (const ask of this.wanted.values()) if (ask.url === url) return ask;
    return undefined;
  }

  /** A seek's frame first: reading ahead waits while one is decoded (it may need the network). */
  private async afterAsks(): Promise<void> {
    while (this.asked > 0 && !this.resting && !this.disposed) {
      await new Promise<void>((done) => this.waiting.push(done));
    }
  }

  /** Decodes the file's asks, each clip's latest, until none is left. */
  private async decode(url: string, file: ScrubFile): Promise<void> {
    file.decoding = true;
    this.asked += 1;
    try {
      const opened = await file.opened;
      if (opened === null) return;
      for (let ask = this.askOf(url); ask !== undefined && this.files.get(url) === file; ask = this.askOf(url)) {
        // An ask of the clip that comes meanwhile is decoded next.
        this.wanted.delete(ask.clipId);
        const wrapped = await opened.frames.getCanvas(ask.ms / 1000);
        // What the frame's read added counts at once: files away from the playhead go for it.
        this.reconcile(this.working());
        if (wrapped === null) continue;
        const bitmap = await createImageBitmap(wrapped.canvas);
        if (this.disposed) {
          bitmap.close();
          return;
        }
        this.decoded.get(ask.clipId)?.bitmap.close();
        this.decoded.set(ask.clipId, {
          url,
          askedMs: ask.ms,
          fromMs: wrapped.timestamp * 1000,
          toMs: (wrapped.timestamp + wrapped.duration) * 1000,
          bitmap,
          picture: { source: bitmap, width: bitmap.width, height: bitmap.height, moving: false },
        });
        this.onFrame();
      }
    } catch {
      // A frame the cache cannot decode: its clip shows what it did before.
    } finally {
      file.decoding = false;
      this.asked -= 1;
      if (this.asked === 0) for (const done of this.waiting.splice(0)) done();
    }
  }

  /** Reads the spans ahead, a few at once, in order and within the budget, until resting. */
  private async readAhead(): Promise<void> {
    this.reading = true;
    try {
      await Promise.all(Array.from({ length: READS_AT_ONCE }, () => this.readNext()));
    } finally {
      this.reading = false;
    }
  }

  /** Builds the strip, a few spans at once, beside the spans' readers so as not to hold them back. */
  private async stripAhead(): Promise<void> {
    this.stripping = true;
    try {
      await Promise.all(Array.from({ length: STRIPS_AT_ONCE }, () => this.stripNext()));
    } finally {
      this.stripping = false;
    }
  }

  /**
   * One of the strip's readers. In the working set it takes the farthest span neither read nor being
   * read: the spans' readers come from the nearest, so the two meet. Past the working set, which is
   * not read ahead, it takes the nearest. Until the strip's limit, or none is left.
   */
  private async stripNext(): Promise<void> {
    while (!this.resting && !this.disposed && this.stripped < STRIP_FRAMES) {
      // Each file's bytes per ms, which the budget counts a strip's reads by, is learnt first.
      const unknown = [...new Set(this.order.map((span) => span.url))].filter(
        (url) => !this.rates.has(url) && !this.unreadable.has(url),
      );
      if (unknown.length > 0) {
        await Promise.all(unknown.map((url) => this.file(url).opened));
        continue;
      }
      const wanted = (span: ScrubSpan) =>
        !this.unreadable.has(span.url) &&
        !this.isRead(span) &&
        !this.inFlight.has(readingKey(span)) &&
        !this.strips.has(readingKey(span)) &&
        !this.inFlight.has(`strip ${readingKey(span)}`);
      const working = this.working();
      const inWorking = new Set(working.map(readingKey));
      const next =
        [...working].reverse().find(wanted) ?? this.order.find((span) => !inWorking.has(readingKey(span)) && wanted(span));
      if (next === undefined || !(await this.buildStrip(next, working))) return;
    }
  }

  private isRead(span: ScrubSpan): boolean {
    return this.files.get(span.url)?.read.has(spanKey(span)) ?? false;
  }

  /**
   * One reader: the next span in order that is neither read nor being read, until none is left
   * within the budget or the files hold it.
   */
  private async readNext(): Promise<void> {
    while (!this.resting && !this.disposed) {
      // Each file is opened once to learn its bytes per ms, which the budget counts by.
      const unknown = [...new Set(this.order.map((span) => span.url))].filter(
        (url) => !this.rates.has(url) && !this.unreadable.has(url),
      );
      if (unknown.length > 0) {
        await Promise.all(unknown.map((url) => this.file(url).opened));
        continue;
      }
      const working = this.working();
      this.reconcile(working);
      const next = working.find((span) => !this.isRead(span) && !this.inFlight.has(readingKey(span)));
      if (next === undefined) return;
      const file = this.file(next.url);
      const opened = await file.opened;
      if (this.resting || this.disposed) return;
      // The file went meanwhile, or another reader took the span: the next is chosen again.
      if (opened === null || this.files.get(next.url) !== file || this.isRead(next) || this.inFlight.has(readingKey(next))) {
        continue;
      }
      // A file at its cache's limit reads in place of what it used least.
      const kept = Math.min(file.held, this.budget);
      const needed = this.needed(next);
      if (!this.reconcile(working, Math.min(this.budget, kept + needed) - kept, file)) return;
      // Let go for room, it is opened again, and its span chosen once it is.
      if (this.files.get(next.url) !== file) continue;
      const reading = readingKey(next);
      this.inFlight.add(reading);
      this.reserved += needed;
      try {
        const ms = await this.readSpan(opened.track, next);
        if (ms !== null && this.files.get(next.url) === file) file.read.set(spanKey(next), ms);
      } finally {
        this.inFlight.delete(reading);
        this.reserved -= needed;
      }
    }
  }

  /**
   * Builds a span's strip: its keyframes from its file's index, the strip's times among them
   * (`stripTimes`), each decoded small, up to the strip's limit. Whether to read on: false when the
   * files have no room for its reads, or its file was let go for room meanwhile; the strip then waits
   * for the playhead to move. A span whose strip cannot be read keeps an empty one.
   */
  private async buildStrip(span: ScrubSpan, working: readonly ScrubSpan[]): Promise<boolean> {
    const key = readingKey(span);
    const reading = `strip ${key}`;
    const { signal } = this.stop;
    const made: Strip["frames"] = [];
    // The places it takes under the strip's limit, each before its frame is decoded, so that readers
    // building strips at once keep to the limit.
    let taken = 0;
    const rate = this.rates.get(span.url) ?? 0;
    // Room for one of its frames first, before its file is opened. Its own file is never let go for
    // it: opened again, it would need the same room (the playhead's files may hold the budget).
    if (!this.reconcile(working, STRIP_READ_MS * rate, undefined, span.url)) return false;
    const file = this.file(span.url);
    this.inFlight.add(reading);
    try {
      const opened = await file.opened;
      if (opened === null) return true;
      if (this.files.get(span.url) !== file) return false;
      const mb = await this.mediabunny();
      const sink = new mb.EncodedPacketSink(opened.track);
      const keys: number[] = [];
      let packet =
        (await sink.getKeyPacket(span.fromMs / 1000, { metadataOnly: true })) ??
        (await sink.getFirstPacket({ metadataOnly: true }));
      while (packet !== null && packet.timestamp * 1000 < span.toMs) {
        keys.push(packet.timestamp * 1000);
        packet = await sink.getNextKeyPacket(packet, { metadataOnly: true });
      }
      const times = stripTimes(keys, span);
      if (!this.reconcile(working, times.length * STRIP_READ_MS * rate, undefined, span.url)) return false;
      for (const ms of times) {
        await this.afterAsks();
        // Stopped for playing; or its file was let go for room, and the strip waits for a move.
        if (this.resting || this.disposed) return true;
        if (this.files.get(span.url) !== file) return false;
        if (this.stripped >= STRIP_FRAMES) break;
        this.stripped += 1;
        taken += 1;
        const wrapped = await opened.strip.getCanvas(ms / 1000);
        if (wrapped === null) continue;
        const bitmap = await createImageBitmap(wrapped.canvas);
        made.push({ ms, bitmap, picture: { source: bitmap, width: bitmap.width, height: bitmap.height, moving: false } });
      }
      const frames = made.splice(0);
      this.strips.set(key, { url: span.url, fromMs: span.fromMs, toMs: span.toMs, frames });
      // A clip the last paint asked for, in this span, shows this strip's frame now if it has no
      // frame of its own yet: the paused preview is told, as for a decoded frame.
      const shown = (ask: ScrubAsk) => ask.url === span.url && ask.ms >= span.fromMs && ask.ms < span.toMs;
      if (frames.length > 0 && this.asks.some(shown)) this.onFrame();
      // The places taken for frames that came to nothing are given back.
      this.stripped -= taken - frames.length;
      taken = 0;
      return true;
    } catch (error) {
      // A strip Play stopped is built again once paused.
      if (!stoppedForPlay(error, signal) && this.files.get(span.url) === file) {
        this.strips.set(key, { url: span.url, fromMs: span.fromMs, toMs: span.toMs, frames: [] });
      }
      return true;
    } finally {
      // A strip left unfinished gives its frames and its places back.
      for (const frame of made) frame.bitmap.close();
      this.stripped -= taken;
      this.inFlight.delete(reading);
    }
  }

  /** The bytes a span's read is expected to add, from its file's bytes per ms. */
  private needed(span: ScrubSpan): number {
    return (span.toMs - span.fromMs + READ_PAST_MS) * (this.rates.get(span.url) ?? 0);
  }

  /** What the files hold together, each at most the budget its cache keeps. */
  private holding(): number {
    let total = 0;
    for (const file of this.files.values()) total += Math.min(file.held, this.budget);
    return total;
  }

  /** The spans to hold: in order and readable, within the budget by their files' bytes per ms. */
  private working(): ScrubSpan[] {
    // A file that cannot be opened is left out: its clips show what they did before.
    const readable = this.order.filter((span) => !this.unreadable.has(span.url));
    return withinBudget(readable, (url) => this.rates.get(url) ?? 0, this.budget);
  }

  /**
   * Keeps what the files hold within the budget, the spans being read counted in full and `extra`
   * more for a read about to start, by letting files go (see `victim`). It runs as the playhead
   * moves, at each reader's turn and after each frame decoded, whether a span is left to read or
   * not; false when no file can go. It stops once `reading` is let go, to be opened again, and never
   * lets the file at `keep` go.
   */
  private reconcile(working: readonly ScrubSpan[], extra = 0, reading?: ScrubFile, keep?: string): boolean {
    const inWorking = new Map<string, Set<string>>();
    for (const each of working) inWorking.set(each.url, (inWorking.get(each.url) ?? new Set()).add(spanKey(each)));
    while (this.holding() + this.reserved + extra > this.budget) {
      const victim = this.victim(inWorking, keep);
      if (victim === undefined) return false;
      this.drop(...victim);
      if (victim[1] === reading) return true;
    }
    return true;
  }

  /**
   * The file to let go for room: first one with no span in the working set, the one holding most;
   * then one holding at least as much outside it as in it, which is opened again, so what it read
   * for an earlier playhead goes. A file at the playhead, or one a frame is asked of, is kept.
   */
  private victim(inWorking: ReadonlyMap<string, ReadonlySet<string>>, keep?: string): [string, ScrubFile] | undefined {
    let outside: [string, ScrubFile] | undefined;
    let stale: [string, ScrubFile] | undefined;
    let staleMs = 0;
    for (const [url, file] of this.files) {
      if (url === keep || this.here.has(url) || file.decoding || this.askOf(url) !== undefined) continue;
      const keys = inWorking.get(url);
      if (keys === undefined) {
        if (outside === undefined || file.held > outside[1].held) outside = [url, file];
        continue;
      }
      let inMs = 0;
      let outMs = 0;
      for (const [key, ms] of file.read) {
        if (keys.has(key)) inMs += ms;
        else outMs += ms;
      }
      if (outMs > 0 && outMs >= inMs && outMs > staleMs) {
        stale = [url, file];
        staleMs = outMs;
      }
    }
    return outside ?? stale;
  }

  /**
   * Reads a span's packets, from the keyframe before it to a little past its end, so its bytes are
   * held; the ms read, or null when it stopped for playing. A span that cannot be read counts as
   * read, and is left.
   */
  private async readSpan(track: InputVideoTrack, span: ScrubSpan): Promise<number | null> {
    const { signal } = this.stop;
    try {
      const mb = await this.mediabunny();
      const sink = new mb.EncodedPacketSink(track);
      const first = (await sink.getKeyPacket(span.fromMs / 1000)) ?? (await sink.getFirstPacket());
      if (first === null) return 0;
      const endMs = span.toMs + READ_PAST_MS;
      for await (const packet of sink.packets(first)) {
        await this.afterAsks();
        if (this.resting || this.disposed) return null;
        if (packet.timestamp * 1000 >= endMs) break;
      }
      return endMs - first.timestamp * 1000;
    } catch (error) {
      return stoppedForPlay(error, signal) ? null : 0;
    }
  }
}
